import { Check, ChevronDown, Plus } from 'lucide-react'
import type { ReactNode } from 'react'

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { OverlayScrollY } from '@/components/widget/overlay-scroll-y'
import { cn } from '@/lib/utils'
import { Exchange } from '@/types/generated/common/types'

export interface FilterOption<T> {
  value: T
  label: string
  icon?: ReactNode
}

interface FilterRowProps<T> {
  categoryLabel?: string
  options: FilterOption<T>[]
  activeSet: Set<T>
  allLabel: string
  onToggle: (value: T) => void
  onReset: () => void
  collapsed: boolean
  trailing?: ReactNode
}

export function FilterRow<T>({
  categoryLabel,
  options,
  activeSet,
  allLabel,
  onToggle,
  onReset,
  collapsed,
  trailing,
}: FilterRowProps<T>) {
  const visibleOptions = collapsed
    ? options.filter((option) => activeSet.has(option.value))
    : options

  if (collapsed && visibleOptions.length === 0 && !trailing && !categoryLabel) return null

  const chipClassName = (active: boolean) =>
    cn(
      'flex min-w-7 items-center justify-center gap-1 rounded-sm px-1.5 py-1 text-center text-xs transition-colors',
      active
        ? 'bg-emphasis text-emphasis-foreground'
        : 'hover:bg-foreground/5 text-foreground/60 hover:text-foreground'
    )

  return (
    <div className="flex flex-wrap items-center gap-1">
      {categoryLabel ? (
        <span className="text-foreground/30 mr-0.5 text-[10px] font-semibold tracking-tight">
          {categoryLabel}
        </span>
      ) : null}
      {!collapsed ? (
        <button
          type="button"
          aria-pressed={activeSet.size === 0}
          onClick={onReset}
          className={chipClassName(activeSet.size === 0)}
        >
          {allLabel}
        </button>
      ) : null}
      {visibleOptions.map(({ value, label, icon }) => (
        <button
          key={String(value)}
          type="button"
          aria-pressed={activeSet.has(value)}
          onClick={() => onToggle(value)}
          className={chipClassName(activeSet.has(value))}
        >
          {icon}
          {label}
        </button>
      ))}
      {trailing}
    </div>
  )
}

interface FilterMenuProps<T> {
  /** Static name of the dimension, e.g. "Exchange". Omitted when the trigger
   * sits under its own field label (the side panel). */
  label?: string
  /** Leading glyph inside the trigger. */
  icon?: ReactNode
  /** Full-width field trigger for the side panel, instead of a header chip. */
  block?: boolean
  /** Shown after the label — the current selection, or the all-label. */
  summary: string
  options: FilterOption<T>[]
  activeSet: Set<T>
  allLabel: string
  onToggle: (value: T) => void
  onReset: () => void
}

// Multi-select dimensions that don't earn permanent chip real estate live in
// here, so the header stays one row of class chips plus this tools cluster.
export function FilterMenu<T>({
  label,
  icon,
  block = false,
  summary,
  options,
  activeSet,
  allLabel,
  onToggle,
  onReset,
}: FilterMenuProps<T>) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'border-foreground/10 text-foreground/60 hover:text-foreground flex items-center gap-1 rounded-sm border px-2 py-1.5 text-xs whitespace-nowrap transition-colors',
            block && 'bg-foreground/5 w-full gap-2 border-transparent px-2.5 py-2',
            activeSet.size > 0 && 'border-foreground/50 text-foreground'
          )}
        >
          {icon}
          {label ? <span className="text-foreground/35">{label}</span> : null}
          <span className={cn(block && 'min-w-0 flex-1 truncate text-left')}>{summary}</span>
          <ChevronDown className="size-3 shrink-0" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={block ? 'start' : 'end'}
        className={cn('p-1', !block && 'w-48')}
        // Block (side panel) menus match their trigger. Inline, because PopoverContent's own
        // w-72 isn't merged away by a w-(--var) class.
        style={block ? { width: 'var(--radix-popover-trigger-width)' } : undefined}
      >
        {/* Portaled to <body>, outside the wizard Dialog's scroll lock: see `ownScroll`. */}
        <OverlayScrollY scrollButtons ownScroll scrollerClassName="flex max-h-56 flex-col gap-0.5">
          <button
            type="button"
            aria-pressed={activeSet.size === 0}
            onClick={onReset}
            className={cn(
              'hover:bg-foreground/5 flex items-center gap-2 rounded-sm px-3 py-2 text-left text-xs'
            )}
          >
            <span className="min-w-0 flex-1 truncate">{allLabel}</span>
            {activeSet.size === 0 ? <Check className="text-foreground size-3.5 shrink-0" /> : null}
          </button>
          {options.map(({ value, label: optionLabel, icon }) => {
            const active = activeSet.has(value)
            return (
              <button
                key={String(value)}
                type="button"
                aria-pressed={active}
                onClick={() => onToggle(value)}
                className={cn(
                  'hover:bg-foreground/5 flex items-center gap-2 rounded-sm px-3 py-2 text-left text-xs'
                )}
              >
                {icon}
                <span className="min-w-0 flex-1 truncate">{optionLabel}</span>
                {active ? (
                  <Check className="text-foreground size-3.5 shrink-0" aria-hidden="true" />
                ) : null}
              </button>
            )
          })}
        </OverlayScrollY>
      </PopoverContent>
    </Popover>
  )
}

interface ExchangeOverflowPopoverProps {
  options: FilterOption<Exchange>[]
  activeSet: Set<Exchange>
  onToggle: (exchange: Exchange) => void
}

export function ExchangeOverflowPopover({
  options,
  activeSet,
  onToggle,
}: ExchangeOverflowPopoverProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'hover:bg-foreground/5 text-foreground/60 hover:text-foreground flex items-center rounded-sm px-1 py-1.5 text-xs transition-colors'
          )}
        >
          <Plus className="size-3" aria-hidden="true" />
          {options.length}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-48 p-1">
        {/* Portaled to <body>, outside the wizard Dialog's scroll lock: see `ownScroll`. */}
        <OverlayScrollY scrollButtons ownScroll scrollerClassName="flex max-h-56 flex-col gap-0.5">
          {options.map(({ value, label, icon }) => {
            const active = activeSet.has(value)
            return (
              <button
                key={String(value)}
                type="button"
                aria-pressed={active}
                onClick={() => onToggle(value)}
                className={cn(
                  'hover:bg-foreground/5 flex items-center gap-2 rounded-sm px-3 py-2 text-left text-xs'
                )}
              >
                {icon}
                <span className="min-w-0 flex-1 truncate">{label}</span>
                {active ? (
                  <Check className="text-foreground size-3.5 shrink-0" aria-hidden="true" />
                ) : null}
              </button>
            )
          })}
        </OverlayScrollY>
      </PopoverContent>
    </Popover>
  )
}
